import { Period } from '../Sessions/SessionPeriods';
import { TeamLabel, labels } from '../Sessions/SessionTeamSpecification';
import { ClassType } from '../../api';
import { TSession } from '../Sessions/Sessions';
import { LineupEntry, Reference, SessionEvent, TimeSpan, timeSpanToSeconds } from '../../services/sessionModels';
import { EventPlayerType as SessionPlayerType, EventReason as SessionEventReason, EventType as SessionEventType } from '../../services/sessionsPrepare';

export const timeSpanToMs = (value?: TimeSpan | null) => Math.round(timeSpanToSeconds(value) * 1000);

export type LabelMoment = {
  timestamp: number;
  labels: Array<TeamLabel>;
  isFirstOfPeriod: boolean;
  isSubstitution?: boolean;
};
// At the start of each period, we label [key] frames with the expected labels of where [value] contains teamlabel id's
const framesEachPeriod: Map<number, Array<ClassType>> = new Map<number, Array<ClassType>>([
  [3, [ClassType.HomeKeeper, ClassType.HomePlayer, ClassType.AwayKeeper, ClassType.AwayPlayer, ClassType.Referee]], // 3 frames, all team labels
  [2, [ClassType.HomeKeeper, ClassType.AwayKeeper]], // 2 frames, keepers only
]);
const msBetweenFramesAtStartPeriod: number = 60000;
const msBetweenFramesDuringPeriod: number = 15 * 60000; //After the initial frames we label every X seconds
const maxMsBeforeEndPeriod: number = 10 * 60000; //Do not label the last x minutes of the period, that has no use

const findLineupEntry = (event: SessionEvent, lineup: Array<LineupEntry>) => {
  return lineup.find((l) => l.inEventId === event.id);
};

function teamspecMoments(session: TSession | undefined | null) {
  const homeTeam: Reference | undefined | null = session?.homeTeam;
  const periods: Array<Period> = session?.periods || [];
  const teamLabels: TeamLabel[] = labels(session);
  const events: SessionEvent[] = session?.events || [];
  const addSecondsOffset: number = timeSpanToSeconds(session?.startAndFinishOffset);

  // State and setters for debounced value
  const update: Array<LabelMoment> = [];
  const secondsPeriodOffset: number = addSecondsOffset || 3;

  // Add moments from Periods
  periods
    ?.slice()
    ?.sort((a, b) => timeSpanToMs(a.startTime) - timeSpanToMs(b.startTime))
    ?.forEach((period) => {
      let tStart: number = timeSpanToMs(period.startTime);
      let tEnd: number = timeSpanToMs(period.endTime);
      let tLastAdded: number = tStart;

      let momentCount: number = 0;
      framesEachPeriod.forEach((value: Array<ClassType>, key: number) => {
        // Create the labels list based on the set id's
        const useLabels: Array<TeamLabel> = [];
        value.forEach((classType: ClassType) => {
          const foundLabel = teamLabels.find((l) => l.classType === classType);
          if (foundLabel !== undefined) {
            useLabels.push(foundLabel);
          }
        });

        // Add moments to our list, amount = key from map
        for (let i: number = 0; i < key; ++i) {
          const isFirstOfPeriod: boolean = momentCount === 0;
          let momentTs: number = tStart + momentCount * msBetweenFramesAtStartPeriod;
          momentTs = isFirstOfPeriod ? Math.max(0, momentTs - secondsPeriodOffset*1000) : momentTs;

          //check lineup impacting events and change required amount of each class
          let teamLabelCopy: TeamLabel[] = [];
          useLabels.forEach((t: TeamLabel) =>
            teamLabelCopy.push({
              classType: t.classType,
              color: t.color,
              requiredCount: t.requiredCount,
              hotkey: t.hotkey,
              label: t.label,
            })
          );

          events
            ?.filter(
              (event) =>
                event.type === SessionEventType.LineupChange &&
                (event.reason === SessionEventReason.Injury || event.reason === SessionEventReason.RedCard)
            )
            ?.forEach((event: SessionEvent) => {
              if (timeSpanToMs(event.timestamp) > momentTs) return;

              //convert our event type to our classtype (todo; refactor)
              let eventClassType: ClassType | undefined = undefined;
              if (event.teamId === homeTeam?.id) {
                if (event.out && event.out.type === SessionPlayerType.Player) {
                  eventClassType = ClassType.HomePlayer;
                }
                if (event.out && event.out.type === SessionPlayerType.Keeper) {
                  eventClassType = ClassType.HomeKeeper;
                }
              } else {
                if (event.out && event.out.type === SessionPlayerType.Player) {
                  eventClassType = ClassType.AwayPlayer;
                }
                if (event.out && event.out.type === SessionPlayerType.Keeper) {
                  eventClassType = ClassType.AwayKeeper;
                }
              }

              if (eventClassType) {
                const foundLabel = teamLabelCopy.find((tl: TeamLabel) => tl.classType === eventClassType);
                if (foundLabel !== undefined) {
                  foundLabel.requiredCount = foundLabel.requiredCount - 1;
                }
              }
            });

          //add moment
          if (momentTs < tEnd) {
            const newMoment: LabelMoment = {
              timestamp: momentTs,
              isFirstOfPeriod: isFirstOfPeriod,
              labels: teamLabelCopy,
            };
            update.push(newMoment);
            tLastAdded = momentTs;
          }
          momentCount++;
        }
      });

      // After initial frames, we add frames every x seconds using all team labels
      tLastAdded += msBetweenFramesDuringPeriod;
      while (tLastAdded < tEnd - maxMsBeforeEndPeriod) {
        const momentTs: number = tLastAdded;
        // Create the labels list
        const useLabels: Array<TeamLabel> = teamLabels;

        //check lineup impacting events and change required amount of each class
        let teamLabelCopy: TeamLabel[] = [];
        useLabels.forEach((t: TeamLabel) =>
          teamLabelCopy.push({
            classType: t.classType,
            color: t.color,
            requiredCount: t.requiredCount,
            hotkey: t.hotkey,
            label: t.label,
          })
        );

        events?.forEach((event: SessionEvent) => {
          if (timeSpanToMs(event.timestamp) > momentTs || event.reason === SessionEventReason.Substitution) return;

          //convert our event type to our classtype (todo; refactor)
          let eventClassType: ClassType | undefined = undefined;
          if (event.teamId === homeTeam?.id) {
            if (event.out && event.out.type === SessionPlayerType.Player) {
              eventClassType = ClassType.HomePlayer;
            }
            if (event.out && event.out.type === SessionPlayerType.Keeper) {
              eventClassType = ClassType.HomeKeeper;
            }
          } else {
            if (event.out && event.out.type === SessionPlayerType.Player) {
              eventClassType = ClassType.AwayPlayer;
            }
            if (event.out && event.out.type === SessionPlayerType.Keeper) {
              eventClassType = ClassType.AwayKeeper;
            }
          }

          if (eventClassType) {
            const foundLabel = teamLabelCopy.find((tl: TeamLabel) => tl.classType === eventClassType);
            if (foundLabel !== undefined) {
              foundLabel.requiredCount = foundLabel.requiredCount - 1;
            }
          }
        });

        const newMoment: LabelMoment = {
          timestamp: tLastAdded,
          isFirstOfPeriod: false,
          labels: teamLabelCopy,
        };
        update.push(newMoment);
        tLastAdded += msBetweenFramesDuringPeriod;
      }
    });

  // Add moments from events
  events?.forEach((event: SessionEvent) => {
    if (event.reason === SessionEventReason.Substitution && !event.offscreen) {
      const eventTeam = [session?.homeTeam, session?.awayTeam].find((team) => team && team.id === event.teamId);
      const teamLabelName: string = event.in?.type + ' ' + eventTeam?.name;
      const subLabel = teamLabels.find((l) => l.label.toLowerCase() === teamLabelName.toLowerCase());
      const lineupEntry = findLineupEntry(event, session?.lineup || []);
      const label =
        lineupEntry && lineupEntry.player
          ? lineupEntry.shirtNumber + '. ' + (lineupEntry.player.name || '')
          : subLabel?.label || '-';
      if (subLabel !== undefined) {
        const newMoment: LabelMoment = {
          timestamp: timeSpanToMs(event.timestamp),
          isFirstOfPeriod: false,
          labels: [{ ...subLabel, label: label, requiredCount: 1 }],
          isSubstitution: true,
        };
        update.push(newMoment);
      }
    }
  });

  // Update our moments
  //sort all moments based on their timestamps
  update.sort((a: LabelMoment, b: LabelMoment) => a.timestamp - b.timestamp);
  return update;
}

export default teamspecMoments;
